import React, { useContext } from "react";
import TrackingContext from "./context/TrackingContext";
import styled from "styled-components";
import A from "./common/A";
import Link from "./utils/Link";
import { fontSizing, media, spacing, colors, font } from "./theme";
import DefaultButton from "./common/Button";
import Content from "./common/Content";
import CookieModal from "./CookieModal";
import Headline from "./common/Headline";
import { defaultLinkStyling } from "./common/A";
import FocusTrap from "focus-trap-react";

const Container = styled.div`
  background: ${colors.white};
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99;
  box-shadow: 0px -10px 5px -4px rgba(0, 0, 0, 0.75);
  @media all and (-ms-high-contrast: none) {
    box-shadow: rgba(0, 0, 0, 0.75) 0px -1px 5px;
  }
  @media print {
    display: none;
  }

  @media screen and ${media.mobile} {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 60vh;
  }
`;

const StyledContent = styled(Content)`
  display: flex;
  flex-direction: row;
  ${spacing("margin-top", 32)}
  ${spacing("margin-bottom", 32)}
  @media screen and ${media.mobile}{
    flex-direction: column;
  }
`;

const Button = styled(DefaultButton)`
  margin-left: 16px;
  ${fontSizing("text", 24)}
  @media screen and ${media.mobile} {
    margin-left: 0;
    width: 100%;
    flex-wrap: wrap;
    white-space: initial;
  };
  white-space: nowrap;
`;

const TextContainer = styled.div`
  font-size: ${font.sizeXXXXS};
`;
const OptionsContainer = styled.div`
  display: grid;
  margin: auto;
`;
const LinksContainer = styled.div`
  & > * {
    margin: 8px 16px;
    min-width: 100%;
    @media screen and ${media.mobile} {
      margin-left: auto;
      margin-right: 0px;
    }
  }
`;
const CookingSettingsButton = styled.button`
  color: ${colors.blueDark};
  font-size: 18px;
  font-family: "Brandon Text", sans-serif;
  outline: none;
  ${defaultLinkStyling};
  border: #ffffff;
  background-color: #ffffff;
  cursor: pointer;
  padding-left: 0px;
`;
const FloatingCookieButton = styled.button`
  position: fixed;
  display: flex;
  gap: 1em;
  align-items: center;
  bottom: 20px;
  left: 0;
  background-color: #f0f4f5;
  color: ${colors.darkgrey};
  z-index: 100;
  font-size: ${font.sizeXXXS};
  line-height: 20px;
  font-weight: 600;
  padding: 10px;
  border-radius: 0 5px 5px 0;
  transform: translateX(-4.5em);
  transition: transform 0.3s ease;
  border: none;

  svg {
    min-width: 20px;
    min-height: 20px;
  }
  &:hover {
    transform: translateX(0px);
    cursor: pointer;
  }
`;
const StyledDescription = styled.div`
  @media screen and ${media.tablet} {
    margin-left: auto;
    overflow-y: auto;
    max-height: 25vh;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin: auto;

  @media screen and ${media.mobile} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

export default function CookieBar() {
  const {
    showCookieBar,
    allowTracking,
    disallowTracking,
    openModal,
    showModal
  } = useContext(TrackingContext);

  // useEffect(() => {
  //   document.activeElement.dispatchEvent(
  //     new KeyboardEvent("keypress", {
  //       key: "Tab"
  //     })
  //   );
  // }, []);

  return (
    <div>
      {showCookieBar ? (
        <FocusTrap
          active={!showModal}
          focusTrapOptions={{ initialFocus: "#btttn", allowOutsideClick: true }}
        >
          <Container>
            <StyledContent
              padded
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookieEinstellungen"
            >
              <TextContainer>
                <Headline size={4} level={2} id="btttn" tabIndex={0}>
                  Ihre Einstellungen zu Cookies für diese Website
                </Headline>
                <StyledDescription>
                  Um unsere Website optimal zu gestalten und zu verbessern
                  verwenden wir Cookies. Wir nutzen Cookies zum Speichern Ihrer
                  Anmeldedaten für eine sichere Anmeldung, zur Erhebung
                  statistischer Daten und zur Optimierung der
                  Website-Funktionen. Klicken Sie auf „Alle akzeptieren“, um in
                  alle Verarbeitungen Ihrer personenbezogenen Daten
                  einzuwilligen bzw. „Alle ablehnen“, sofern Sie Ihre
                  Einwilligung in die Verarbeitungen nicht erteilen möchten oder
                  klicken Sie unten auf "Cookie Einstellungen verwalten“, um
                  eine detaillierte Beschreibung der von uns verwendeten Arten
                  von Cookies und Informationen über die entsprechenden Anbieter
                  zu erhalten sowie diese Einstellungen aufzurufen, um Cookies
                  jederzeit auch nachträglich abzuwählen bzw. Ihre Einwilligung
                  in das Setzen der Cookies zu widerrufen (z.B. über den
                  Fingerprint-Button unten links auf der Website). Bitte
                  beachten Sie, dass eine Einwilligung in das Setzen
                  essentieller bzw. technisch notwendiger Cookies nicht
                  erforderlich ist. Weitere Informationen finden Sie in unserer
                  Datenschutzerklärung.
                </StyledDescription>
              </TextContainer>
              <OptionsContainer>
                <ButtonWrapper>
                  <span>
                    <Button
                      tabIndex={0}
                      tiny
                      onClick={() => allowTracking()}
                      aria-label="Alle Akzeptieren - Dialog mit Informationen zum Datenschutz und Widerspruchshinweisen schließen."
                    >
                      ALLE AKZEPTIEREN
                    </Button>
                  </span>
                  <Button
                    tabIndex={0}
                    tiny
                    onclick={() => disallowTracking()}
                    aria-label="Alle Ablehnen - Dialog mit Informationen zum Datenschutz und Widerspruchshinweisen schließen."
                  >
                    ALLE ABLEHNEN
                  </Button>{" "}
                </ButtonWrapper>{" "}
                <LinksContainer>
                  <div>
                    <CookingSettingsButton
                      onClick={() => openModal()}
                      tabIndex={0}
                    >
                      Cookie-Einstellungen verwalten
                    </CookingSettingsButton>
                  </div>
                  <div>
                    <Link route="dataPrivacy">
                      <A tabIndex={0}>Datenschutzerklärung</A>
                    </Link>
                  </div>
                  <div>
                    <Link route="impressum">
                      <A tabIndex={0}>Impressum</A>
                    </Link>{" "}
                  </div>
                </LinksContainer>
              </OptionsContainer>
            </StyledContent>
          </Container>
        </FocusTrap>
      ) : (
        <FloatingCookieButton onClick={openModal}>
          <span>Cookies</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
          >
            <path
              d="M9.91943 20C9.17299 20 8.44313 19.8976 7.74645 19.6928C7.04976 19.4881 6.40284 19.1809 5.82228 18.8055C5.24171 18.43 4.77725 17.9693 4.41232 17.4573C4.03081 17.4573 3.69905 17.3208 3.41706 17.0819C3.13507 16.843 2.90284 16.57 2.72038 16.2457C1.85782 15.6826 1.19431 14.8464 0.71327 13.7372C0.232228 12.628 0 11.4334 0 10.1365C0 9.0273 0.165877 8.02048 0.514218 7.15017C0.862559 6.27986 1.32701 5.61433 1.90758 5.17065C2.4218 4.12969 3.08531 3.22526 3.8981 2.45734C4.7109 1.68942 5.60664 1.05802 6.6019 0.648464C7.59716 0.238908 8.64218 0 9.73697 0C10.9645 0 12.1422 0.170648 13.2701 0.52901C14.3981 0.887372 15.4265 1.36519 16.3554 1.99659C17.2844 2.62799 18.1137 3.34471 18.8104 4.16382C19.5071 4.98293 20.0379 5.85324 20.4194 6.80887C20.8009 7.7645 21 8.72014 21 9.7099C21 10.6314 20.8839 11.5188 20.6517 12.372C20.4194 13.2253 20.1043 13.9932 19.6896 14.6928C19.2749 15.3925 18.8104 15.9386 18.263 16.3652C18.0308 16.9113 17.6493 17.372 17.1351 17.7474C16.6209 18.1229 16.0569 18.3959 15.41 18.5666C14.763 18.7372 14.1327 18.8396 13.4858 18.8396C13.0379 19.215 12.5237 19.5051 11.91 19.7099C11.2962 19.9147 10.6327 20.0171 9.90284 20.0171L9.91943 20ZM9.83649 17.5939C10.218 17.5939 10.5829 17.5427 10.9479 17.4573C11.3128 17.372 11.6611 17.2355 11.9929 17.0648C11.1967 16.7065 10.5498 16.1263 10.1019 15.3072L10.9313 14.8123C11.2133 15.3242 11.628 15.6997 12.1588 15.9727C12.6896 16.2457 13.2701 16.3652 13.9005 16.3652C14.6469 16.3652 15.3436 16.1945 15.9905 15.8703C16.6374 15.5461 17.2346 15.0853 17.7654 14.4881C18.2962 13.8908 18.7275 13.1911 19.0924 12.372C19.4573 11.5529 19.6896 10.6655 19.8223 9.69283C19.8223 8.7372 19.6232 7.83276 19.2417 6.97952C18.8602 6.12628 18.3128 5.3413 17.6327 4.62457C16.9526 3.90785 16.1896 3.29351 15.327 2.7645C14.4645 2.23549 13.5521 1.843 12.6066 1.5529C11.6611 1.2628 10.7156 1.12628 9.77014 1.12628C8.94076 1.12628 8.14455 1.2628 7.38152 1.51877C6.61848 1.77474 5.90521 2.16724 5.24171 2.67918C4.5782 3.19113 4.03081 3.82253 3.58294 4.59044H3.73223C4.03081 4.59044 4.36256 4.64164 4.74408 4.72696C5.12559 4.81229 5.50711 4.9488 5.9218 5.11945L5.54028 6.04096C5.00948 5.80205 4.51185 5.68259 4.04739 5.68259C3.46682 5.68259 2.96919 5.88737 2.53791 6.29693C2.10664 6.70648 1.77488 7.23549 1.54265 7.90102C1.31043 8.56655 1.19431 9.28328 1.19431 10.0341C1.19431 10.9044 1.36019 11.6553 1.67536 12.2696C1.99052 12.884 2.43839 13.413 3.01896 13.8567C3.59953 14.3003 4.27962 14.727 5.09242 15.1195C5.54028 15.6655 6.02133 16.1263 6.55213 16.5017C7.08294 16.8771 7.63033 17.1502 8.19431 17.3549C8.75829 17.5597 9.32228 17.6451 9.88626 17.6451L9.83649 17.5939ZM3.79858 11.4676C3.79858 11.4676 3.66588 11.4505 3.59953 11.3993C3.53318 11.3481 3.46682 11.2799 3.38389 11.1775C3.35071 11.1263 3.30095 11.0751 3.25118 11.041C3.05213 10.8874 2.95261 10.6485 2.95261 10.3413C2.95261 10.2389 3.00237 10.1024 3.11848 9.9488C3.2346 9.79522 3.35071 9.6587 3.48341 9.53925C3.61611 9.41979 3.73223 9.35153 3.83175 9.35153C4.06398 9.35153 4.29621 9.45392 4.54502 9.67577C4.79384 9.89761 4.92654 10.1024 4.92654 10.3072C4.92654 10.4096 4.86019 10.5631 4.7109 10.7338C4.56161 10.9044 4.41232 11.0921 4.22986 11.2287C4.04739 11.3652 3.91469 11.4505 3.81517 11.4505L3.79858 11.4676ZM5.80569 4.09556C5.42417 4.09556 5.12559 4.0273 4.90995 3.90785C4.69431 3.7884 4.61137 3.53242 4.61137 3.157C4.61137 2.69625 4.76066 2.30375 5.07583 1.92833C5.391 1.5529 5.77251 1.38225 6.25355 1.38225C6.45261 1.38225 6.66825 1.46758 6.8673 1.65529C7.06635 1.843 7.23223 2.04778 7.36493 2.30375C7.49763 2.55973 7.56398 2.79863 7.56398 3.02048C7.56398 3.48123 7.3981 3.77133 7.06635 3.89078C6.7346 4.01024 6.31991 4.0785 5.80569 4.0785V4.09556ZM6.71801 9.0273C6.25355 9.0273 5.83886 8.90785 5.47393 8.66894C5.109 8.43003 4.92654 8.157 4.92654 7.84983C4.92654 7.73037 4.92654 7.62799 4.95972 7.49147C4.99289 7.35495 5.00948 7.28669 5.04265 7.21843C5.109 7.09898 5.24171 6.96246 5.42417 6.843C5.60664 6.72355 5.7891 6.62116 5.98815 6.53584C6.1872 6.45051 6.36967 6.39932 6.51896 6.39932C6.80095 6.39932 7.03318 6.5529 7.21564 6.87713C7.3981 7.20136 7.49763 7.59386 7.49763 8.05461C7.49763 8.32764 7.41469 8.54949 7.2654 8.7372C7.11611 8.92491 6.93365 9.0273 6.71801 9.0273ZM9.4218 14.1638C8.49289 14.1638 7.79621 14.0444 7.33175 13.8055C6.8673 13.5666 6.58531 13.2082 6.50237 12.6962L6.35308 11.8771C6.30332 11.57 6.41943 11.314 6.71801 11.1092C7.01659 10.9044 7.38152 10.785 7.82938 10.785C7.97867 10.785 8.16114 10.8703 8.39336 11.041C8.62559 11.2116 8.84123 11.4164 9.05687 11.6894C9.27251 11.9625 9.45498 12.2014 9.60427 12.4573C9.75355 12.7133 9.81991 12.9352 9.81991 13.1058C9.81991 13.413 9.78673 13.6689 9.70379 13.8737C9.62085 14.0785 9.52133 14.1809 9.40521 14.1809L9.4218 14.1638ZM11.064 6.843C10.9313 6.843 10.7654 6.75768 10.5498 6.60409C10.3341 6.45051 10.1351 6.24573 9.93602 6.02389C9.73697 5.80205 9.57109 5.56314 9.43839 5.32423C9.30569 5.08532 9.23934 4.89761 9.23934 4.74403C9.23934 4.47099 9.27251 4.23208 9.33886 4.04437C9.40521 3.85665 9.50474 3.77133 9.60427 3.77133C10.4502 3.77133 11.0972 3.87372 11.5118 4.09556C11.9265 4.31741 12.1919 4.64164 12.2583 5.10239L12.391 5.85324C12.4408 6.12628 12.3246 6.34812 12.0592 6.5529C11.7938 6.75768 11.4621 6.843 11.0474 6.843H11.064ZM10.4668 10.6655C10.1351 10.6655 9.83649 10.5461 9.60427 10.3072C9.37204 10.0683 9.25592 9.76109 9.25592 9.41979C9.25592 9.3174 9.32228 9.19795 9.43839 9.06143C9.5545 8.92491 9.72038 8.80546 9.88626 8.70307C10.0521 8.60068 10.2014 8.54949 10.3341 8.54949C10.4502 8.54949 10.5829 8.61775 10.7322 8.75427C10.8815 8.89078 11.0142 9.06143 11.1303 9.24915C11.2464 9.43686 11.2962 9.60751 11.2962 9.74403C11.2962 9.93174 11.1967 10.1365 10.9976 10.3413C10.7986 10.5461 10.6161 10.6655 10.4502 10.6655H10.4668ZM11.7773 3.34471C11.3128 3.34471 11.0806 3.19113 11.0806 2.86689C11.0806 2.57679 11.1635 2.26962 11.346 1.91126C11.5284 1.5529 11.7275 1.2628 11.9597 1.00683C12.1919 0.750853 12.4076 0.614334 12.59 0.614334C12.7725 0.614334 12.9716 0.716724 13.2038 0.921502C13.436 1.12628 13.6351 1.36519 13.8009 1.63823C13.9668 1.91126 14.0498 2.15017 14.0498 2.35495C14.0498 3.00341 13.3033 3.32764 11.7938 3.32764L11.7773 3.34471ZM11.2299 7.98635C11.2299 7.03072 11.4953 6.2628 12.0095 5.68259C12.5237 5.10239 13.2867 4.74403 14.282 4.60751L14.4147 5.51194C13.6185 5.6314 13.0379 5.88737 12.673 6.27986C12.3081 6.67235 12.109 7.25256 12.109 7.98635H11.2299ZM14.8957 14.4198L13.0711 14.1809C12.6066 14.1126 12.3744 13.8396 12.3744 13.3788C12.3744 13.0546 12.4739 12.7133 12.673 12.3549C12.872 11.9966 13.1209 11.6894 13.4194 11.4334C13.718 11.1775 13.9834 11.058 14.2488 11.058C14.5142 11.058 14.7299 11.1775 14.9289 11.3993C15.128 11.6212 15.3104 11.9283 15.4265 12.3038C15.5427 12.6792 15.609 13.0887 15.609 13.5154C15.609 13.7884 15.5427 14.0102 15.3934 14.1809C15.2441 14.3515 15.0782 14.4369 14.8791 14.4027L14.8957 14.4198ZM14.2654 9.0785C14.1327 9.0785 13.9668 9.0273 13.8009 8.90785C13.6351 8.78839 13.4692 8.65188 13.3531 8.49829C13.237 8.34471 13.1706 8.20819 13.1706 8.08874C13.1706 7.91809 13.237 7.73037 13.3697 7.55973C13.5024 7.38908 13.6682 7.21843 13.8507 7.09898C14.0332 6.97952 14.2156 6.91126 14.3815 6.91126C14.6469 6.91126 14.8626 7.03072 15.045 7.26962C15.2275 7.50853 15.3104 7.7645 15.3104 8.00341C15.3104 8.157 15.2441 8.31058 15.128 8.48123C15.0118 8.65188 14.8626 8.78839 14.6967 8.90785C14.5308 9.0273 14.3981 9.0785 14.282 9.0785H14.2654ZM17.6161 9.98293C17.0853 9.98293 16.737 9.93174 16.5877 9.81229C16.4384 9.69283 16.3554 9.47099 16.3554 9.12969C16.3554 8.70307 16.4384 8.31058 16.5877 7.98635C16.737 7.66211 16.936 7.50853 17.1517 7.50853C17.3673 7.50853 17.5829 7.59386 17.8152 7.78157C18.0474 7.96928 18.2464 8.19112 18.4123 8.4471C18.5782 8.70307 18.6611 8.95904 18.6611 9.21502C18.6611 9.45392 18.5782 9.64164 18.3957 9.77816C18.2133 9.91467 17.9645 10 17.6327 10L17.6161 9.98293ZM18.263 7.01365C18.1801 7.01365 18.0806 6.99659 17.981 6.94539C17.8815 6.8942 17.7488 6.82594 17.6327 6.72355L17.3507 6.51877C17.1517 6.36519 16.936 6.2628 16.7204 6.17747C16.5047 6.09215 16.3886 5.9727 16.3886 5.78498C16.3886 5.61433 16.455 5.42662 16.5711 5.23891C16.6872 5.05119 16.8199 4.86348 16.9692 4.67577C16.9692 4.60751 16.9692 4.52218 17.0024 4.38566C17.0355 4.24915 17.0687 4.12969 17.1185 4.01024C17.1682 3.89078 17.2678 3.83959 17.3673 3.83959C17.6327 3.83959 17.8649 3.94198 18.064 4.14676C18.263 4.35154 18.4455 4.60751 18.5616 4.91468C18.6777 5.22184 18.7441 5.52901 18.7441 5.81911C18.7441 6.22867 18.6943 6.51877 18.5948 6.70648C18.4953 6.8942 18.3791 6.99659 18.2464 6.99659L18.263 7.01365Z"
              fill="#1D1D1B"
            />
          </svg>
        </FloatingCookieButton>
      )}
      <CookieModal />
    </div>
  );
}
